import { z } from 'zod';

/**
 * Session Explorer (ENT-4767 / SE-10) API boundary.
 *
 * Contract: knowledge-base `10-session-explorer.md`. The three FE endpoints
 * (`agentInsights/member/session-explorer/{status,sessions,map}/`) answer in
 * camelCase, and the map document itself is `entelligence.session-map/v1`,
 * fetched straight from a presigned S3 GET.
 *
 * The map schema is validated loosely: every object is `passthrough` and
 * optional fields default, so a CLI that adds a field never breaks the page.
 * Only the `schema` discriminator is strict, because a v2 map is not something
 * this renderer can draw.
 */

export const SESSION_MAP_SCHEMA_V1 = 'entelligence.session-map/v1';

export const sessionExplorerStatusSchema = z
  .object({
    success: z.boolean().optional(),
    available: z.boolean().catch(false).default(false),
    mapCount: z.number().catch(0).default(0),
    latestSessionDate: z.string().nullable().catch(null).default(null),
  })
  .passthrough();
export type SessionExplorerStatus = z.infer<typeof sessionExplorerStatusSchema>;

/** Session-list `repos`: the backend sends either plain names or
 * `{name, remote}` objects reduced from the commit summary (controller
 * `_sm_repos`). Normalize both to the display name `SessionList` renders. */
const sessionRepoNameSchema = z
  .union([
    z.string(),
    z
      .object({ name: z.string().optional(), remote: z.string().optional() })
      .passthrough(),
  ])
  .transform((repo) =>
    (typeof repo === 'string' ? repo : (repo.name ?? repo.remote ?? '')).trim()
  )
  .catch('');

export const sessionExplorerSessionSchema = z
  .object({
    sessionId: z.string(),
    sessionDate: z.string().nullable().default(null),
    startedAt: z.string(),
    endedAt: z.string().nullable().default(null),
    durationMs: z.number().default(0),
    agentTool: z.string().default('claude-code'),
    models: z.array(z.string()).default([]),
    repos: z
      .array(sessionRepoNameSchema)
      .default([])
      .transform((repos) => repos.filter((r) => r.length > 0)),
    calls: z.number().default(0),
    requests: z.number().default(0),
    subagents: z.number().default(0),
    errors: z.number().default(0),
    filesTouched: z.number().default(0),
    costUsd: z.number().default(0),
    tokens: z.number().default(0),
    userTurns: z.number().default(0),
    sessionTitle: z.string().nullable().default(null),
    contentHash: z.string().nullable().default(null),
    bytesGz: z.number().nullable().default(null),
    updatedAt: z.string().nullable().default(null),
    // optional signal summary (render when present)
    errorsBySource: z
      .record(z.string(), z.number())
      .optional()
      .catch(undefined),
    retries: z.number().optional().catch(undefined),
    delays: z.number().optional().catch(undefined),
    maxContext: z.number().optional().catch(undefined),
    compactions: z.number().optional().catch(undefined),
    // Stage 2 (ENT-4787), all optional: an older backend omits them.
    role: z.string().nullable().optional().catch(undefined),
    parentSessionId: z.string().nullable().optional().catch(undefined),
    tasks: z.number().optional().catch(undefined),
    outputs: z.record(z.string(), z.number()).optional().catch(undefined),
    prs: z
      .array(
        z
          .object({
            repo: z.string().optional(),
            number: z.number().optional(),
            url: z.string().optional(),
            state: z.string().optional(),
          })
          .passthrough()
      )
      .optional()
      .catch(undefined),
  })
  .passthrough();
export type SessionExplorerSession = z.infer<
  typeof sessionExplorerSessionSchema
>;

export const sessionExplorerSessionsSchema = z
  .object({
    success: z.boolean().optional(),
    sessions: z.array(sessionExplorerSessionSchema).default([]),
    nextCursor: z.string().nullable().default(null),
    hasMore: z.boolean().default(false),
  })
  .passthrough();
export type SessionExplorerSessionsPage = z.infer<
  typeof sessionExplorerSessionsSchema
>;

/** One presigned map in the batch answer. */
export const sessionExplorerMapMetaSchema = z
  .object({
    sessionId: z.string(),
    url: z.string().min(1),
    schemaVersion: z.number().default(1),
    contentHash: z.string().min(1),
    bytesGz: z.number().nullable().default(null),
  })
  .passthrough();
export type SessionExplorerMapMeta = z.infer<
  typeof sessionExplorerMapMetaSchema
>;

/** An id the backend would not sign (unknown, foreign or expired row). */
export const sessionExplorerMapMissingSchema = z
  .object({ sessionId: z.string(), status: z.literal('not_found') })
  .passthrough();

/** One `annotations[sessionId][]` entry: a sidecar note the backend stores
 * next to the immutable map. `review_flagged` means a PR review finding on a
 * file this task touched was later addressed (the author fixed it). `note`
 * is `"<severity> <category>: <repo-relative file>"`, any part may be
 * missing, and never carries comment text. `startTurn`/`endTurn` (1-based
 * user turns) locate the task; `taskNumber` is the backend's own numbering,
 * which can differ from the map's, so it is only a fallback.
 * `githubCommentId`/`repoName`/`prNumber`/`commentUrl` point at the GitHub
 * review comment the finding came from (backend#5631); each is null (or
 * absent, on older backends) when unknown.
 * `verdict`/`causeKind`/`fixTarget`/`skippedStep`/`ruleId` are the developer
 * agent's reflection on the finding (backend#5643): `verdict` is one of
 * `correct|partly_correct|incorrect|uncertain` (an `incorrect` one is never
 * sent as a flag, but is rendered defensively), `causeKind`/`fixTarget` are
 * open snake_case enums, `skippedStep` is short free text (<=160 chars) and
 * `ruleId` a rule-pack id. Each is null (or absent) when there is no
 * assessment.
 * `commentLine`/`commentStartLine` (backend#5652) are the commented line in
 * the PR head (the end line of a multi-line comment) and the start of a
 * multi-line range; null (or absent) when unknown. Numbers only. */
export const sessionMapAnnotationSchema = z
  .object({
    taskNumber: z.number(),
    startTurn: z.number().nullable().catch(null).default(null),
    endTurn: z.number().nullable().catch(null).default(null),
    kind: z.string(),
    note: z.string().catch('').default(''),
    githubCommentId: z.number().nullable().catch(null).default(null),
    repoName: z.string().nullable().catch(null).default(null),
    prNumber: z.number().nullable().catch(null).default(null),
    commentUrl: z.string().nullable().catch(null).default(null),
    verdict: z.string().nullable().catch(null).default(null),
    causeKind: z.string().nullable().catch(null).default(null),
    fixTarget: z.string().nullable().catch(null).default(null),
    skippedStep: z.string().nullable().catch(null).default(null),
    ruleId: z.string().nullable().catch(null).default(null),
    commentLine: z.number().int().nullable().catch(null).default(null),
    commentStartLine: z.number().int().nullable().catch(null).default(null),
  })
  .passthrough();
export type SessionMapAnnotation = z.infer<typeof sessionMapAnnotationSchema>;

/** One `reviewPending[sessionId]` entry (V11): the session has PR review
 * correlations that have not settled final yet, so its annotations overlay
 * is empty for a reason, not because nothing was found. */
export const pendingReviewSchema = z
  .object({
    pendingCorrelations: z.number().catch(0).default(0),
    prs: z
      .array(
        z
          .object({
            repoName: z.string().catch('').default(''),
            prNumber: z.number().nullable().catch(null).default(null),
          })
          .passthrough()
      )
      .catch([])
      .default([]),
  })
  .passthrough();
export type PendingReview = z.infer<typeof pendingReviewSchema>;

/** `map/?session_id=A&session_id=B…` (1-20 ids). */
export const sessionExplorerMapBatchSchema = z
  .object({
    success: z.boolean().optional(),
    expiresIn: z.number().default(300),
    maps: z
      .array(
        z.union([sessionExplorerMapMetaSchema, sessionExplorerMapMissingSchema])
      )
      .default([]),
    // Always present (`{}` when the org is not opted in). An overlay, so a
    // malformed block drops to "no annotations" instead of failing the maps.
    annotations: z
      .record(z.string(), z.array(sessionMapAnnotationSchema))
      .default({})
      .catch({}),
    // Always present (`{}` off the allowlist or on a read failure), like
    // `annotations`.
    reviewPending: z
      .record(z.string(), pendingReviewSchema)
      .default({})
      .catch({}),
  })
  .passthrough();
export type SessionExplorerMapBatch = z.infer<
  typeof sessionExplorerMapBatchSchema
>;
export type SessionExplorerMapItem = SessionExplorerMapBatch['maps'][number];

export const isMapFound = (
  item: SessionExplorerMapItem
): item is SessionExplorerMapMeta =>
  'url' in item && typeof item.url === 'string';

// --- map document ---------------------------------------------------------

const touchSchema = z.enum(['hit', 'read', 'edit']).catch('hit');

/** `targets[].op` / `files[].op` (contract "PR creation, tool categories,
 * file ops, outcome, agent lens"): closed vocabulary, but optional and
 * `.catch(undefined)` since most tool events touch no file at all. */
const fileOpSchema = z
  .enum(['create', 'modify', 'delete', 'rename-from', 'rename-to'])
  .optional()
  .catch(undefined);

const targetSchema = z
  .object({
    file: z.number().optional(),
    scope: z.string().optional(),
    label: z.string().optional(),
    touch: touchSchema,
    op: fileOpSchema,
    // ellie-cli#105: 1-based inclusive [start, end] line ranges of the file
    // right after a successful edit; absent when unknown. Read loosely
    // (`reviewFlags.ts` editLines) so a malformed value never fails the map.
    lines: z.unknown().optional(),
  })
  .passthrough();

const optNum = z.number().optional();
const optStr = z.string().optional();

/** `events[].vcs`: a VCS/PR-hosting operation. `action` is an open
 * vocabulary (git subcommands, `<noun>_<verb>` for gh/glab) except the three
 * closed PR-creation states (`pr_create`, `pr_create_attempt`,
 * `pr_create_failed`), so it stays `z.string()` rather than an enum. */
const vcsSchema = z
  .object({
    provider: z.string(),
    action: z.string(),
    repo: optStr,
    number: optNum,
  })
  .passthrough()
  .optional();

export const sessionMapEventSchema = z
  .object({
    seq: optNum,
    t: z.number().default(0),
    kind: z.string(),
    agent: z.string().default('main'),
    err: z.boolean().default(false),
    tool: optStr,
    verb: optStr,
    targets: z.array(targetSchema).optional(),
    // PR creation, tool categories (contract 2026-09-22)
    vcs: vcsSchema,
    cat: optStr,
    outBytes: optNum,
    dur: optNum,
    server: optStr,
    skill: optStr,
    subagentType: optStr,
    model: optStr,
    child: optStr,
    host: optStr,
    in: optNum,
    cw: optNum,
    cr: optNum,
    out: optNum,
    cost: optNum,
    chars: optNum,
    // signals (contract "Signals"): tool/MCP/subagent failures and retries
    errClass: optStr,
    exitCode: optNum,
    retryOf: optNum,
    attempt: optNum,
    // llm response metadata (never text)
    latency: optNum,
    stop: optStr,
    thinking: z.boolean().optional(),
    toolCalls: optNum,
    textChars: optNum,
    // error events
    source: optStr,
    hook: optStr,
    retry: z.boolean().optional(),
    // delay events
    cause: optStr,
    ms: optNum,
    ref: optNum,
    // compaction events
    trigger: optStr,
    preTokens: optNum,
    postTokens: optNum,
    // Stage 2 (ENT-4787, additive in v1): task number + driving model on
    // every event, prompt shape on user turns (counts only, never content),
    // and the fields of the output kinds (commit / pr_merge / pr_close /
    // pr_comment / ticket / artifact / skill_write / model_switch).
    task: optNum,
    images: optNum,
    urls: optNum,
    repo: optStr,
    sha: optStr,
    files: optNum,
    ins: optNum,
    del: optNum,
    number: optNum,
    url: optStr,
    tracker: optStr,
    action: optStr,
    id: optStr,
    title: optStr,
    bytes: optNum,
    version: optNum,
    name: optStr,
    from: optStr,
    to: optStr,
    // Client-stamped, never read from the wire: `stampTurns` /
    // `stampReviewFlags` overwrite both on the full map before scope/lens.
    // `turn` is the 1-based user turn; `reviewFlags` are the edited files a
    // later-addressed review finding landed on (`annotations`).
    turn: optNum,
    reviewFlags: z
      .array(
        z.object({
          file: z.number(),
          note: z.string(),
          // how the edit was picked (`reviewFlags.ts` EditMatch)
          match: z.enum(['exact', 'last_edit']).optional(),
        })
      )
      .optional()
      .catch(undefined),
  })
  .passthrough();

/** `tasks[]` (Stage 2): the CLI's own segmentation (`source: "segments"`) or
 * one task per user-turn window (`"turns"`). `startSeq`/`endSeq` are
 * inclusive event sequence numbers (seq === index). */
export const sessionMapTaskSchema = z
  .object({
    n: z.number(),
    id: optStr,
    name: optStr,
    source: optStr,
    startSeq: optNum,
    endSeq: optNum,
    startT: optNum,
    endT: optNum,
    firstTurn: optNum,
    lastTurn: optNum,
  })
  .passthrough();

export const sessionMapSchema = z
  .object({
    schema: z.literal(SESSION_MAP_SCHEMA_V1),
    session: z
      .object({
        id: z.string(),
        agentTool: z.string().default('claude-code'),
        cliVersion: optStr,
        startedAt: z.string(),
        durationMs: z.number().default(0),
        models: z.array(z.string()).default([]),
        pathMode: optStr,
        truncated: z.boolean().default(false),
        // one of pr_created | verified | blocked | interrupted |
        // unresolved_errors | completed | incomplete; open string so an
        // unrecognized value degrades to its own label instead of failing.
        outcome: optStr,
        role: z.string().nullable().optional(),
        parentSessionId: z.string().nullable().optional(),
      })
      .passthrough(),
    totals: z
      .object({
        calls: z.number().default(0),
        requests: z.number().default(0),
        userTurns: z.number().default(0),
        subagents: z.number().default(0),
        errors: z.number().default(0),
        tokens: z.number().default(0),
        costUsd: z.number().default(0),
        filesTouched: z.number().default(0),
        errorsBySource: z.record(z.string(), z.number()).default({}),
        errorsByClass: z.record(z.string(), z.number()).default({}),
        retries: optNum,
        delays: optNum,
        delayMs: optNum,
        maxContext: optNum,
        compactions: optNum,
        llmLatencyP50: optNum,
        llmLatencyP95: optNum,
        outputTokens: optNum,
        // PR creation, file ops (contract 2026-09-22)
        prsCreated: optNum,
        filesCreated: optNum,
        filesDeleted: optNum,
      })
      .passthrough(),
    repos: z
      .array(
        z
          .object({ id: z.string(), name: z.string(), remote: optStr })
          .passthrough()
      )
      .default([]),
    files: z
      .array(
        z
          .object({
            id: z.number(),
            repo: z.string(),
            path: z.string(),
            reads: z.number().default(0),
            edits: z.number().default(0),
            hits: z.number().default(0),
            first: optNum,
            // last op seen for this file across the whole doc; a lens must
            // not read this for its own scope (see fileCoverage/recomputeTotals)
            op: fileOpSchema,
          })
          .passthrough()
      )
      .default([]),
    // Confirmed PR/MRs (contract 2026-09-22); `seq` points at the creating
    // event. `totals.prsCreated` = length, kept separately since the backend
    // sends both.
    prs: z
      .array(
        z
          .object({
            provider: z.string(),
            repo: optStr,
            number: z.number(),
            url: z.string(),
            seq: z.number(),
            state: optStr,
          })
          .passthrough()
      )
      .default([]),
    agents: z
      .array(
        z
          .object({
            id: z.string(),
            parent: z.string().nullable().default(null),
            kind: z.string().default('main'),
            subagentType: optStr,
            launchSeq: optNum,
            start: z.number().default(0),
            end: z.number().default(0),
            calls: z.number().default(0),
            requests: z.number().default(0),
            tokens: z.number().default(0),
            cost: z.number().default(0),
            outcome: optStr,
          })
          .passthrough()
      )
      .default([]),
    events: z.array(sessionMapEventSchema).default([]),
    // A malformed tasks block must never fail the whole map: drop it and
    // fall back to per-user-turn windows.
    tasks: z.array(sessionMapTaskSchema).optional().catch(undefined),
  })
  .passthrough();

export type SessionMap = z.infer<typeof sessionMapSchema>;
export type SessionMapEvent = SessionMap['events'][number];
export type SessionMapTarget = NonNullable<SessionMapEvent['targets']>[number];
export type SessionMapFile = SessionMap['files'][number];
export type SessionMapAgent = SessionMap['agents'][number];
export type SessionMapPr = SessionMap['prs'][number];
export type SessionMapTask = z.infer<typeof sessionMapTaskSchema>;
export type FileOp = NonNullable<SessionMapFile['op']>;

// --- Harness ledger (agent optimization) ------------------------------------
// `agentInsights/member/session-explorer/harness/` (backend #5925): the
// agent-optimization harness ledger for one session. Parsed even more
// leniently than the map: every field is optional, a malformed entry is
// dropped on its own (never the whole list), and unknown event types pass
// through untouched. Only `available` is keyed on: anything but `true`
// means "render nothing harness-related".

const hNum = z.number().optional().catch(undefined);
const hStr = z.string().optional().catch(undefined);
const hBool = z.boolean().optional().catch(undefined);

/** An array whose bad entries are dropped one by one. */
const lenientArray = <T extends z.ZodTypeAny>(item: T) =>
  z
    .array(z.unknown())
    .catch([])
    .transform((rows) =>
      rows.flatMap((v) => {
        const r = item.safeParse(v);
        return r.success ? [r.data as z.output<T>] : [];
      })
    );

/** A string-keyed map whose bad values are dropped one by one. */
const lenientRecord = <T extends z.ZodTypeAny>(item: T) =>
  z
    .record(z.unknown())
    .catch({})
    .transform((obj) => {
      const out: Record<string, z.output<T>> = {};
      for (const [k, v] of Object.entries(obj)) {
        const r = item.safeParse(v);
        if (r.success) out[k] = r.data as z.output<T>;
      }
      return out;
    });

export const harnessCardSchema = z
  .object({
    source: hStr,
    item_id: z.union([z.string(), z.number()]).optional().catch(undefined),
    text_snapshot: hStr,
    file: hStr,
    line_start: hNum,
    line_end: hNum,
    shown: hBool,
    times_shown: hNum,
    hooks: z.array(z.string()).optional().catch(undefined),
    first_shown_at: hNum,
    match: z
      .object({ method: hStr, jev_probability: hNum, floor: hNum })
      .passthrough()
      .optional()
      .catch(undefined),
    outcome: z
      .object({
        relevance: hStr,
        verdict: hStr,
        acted_on: hBool,
        already_handled: hBool,
        context_enough: hStr,
      })
      .passthrough()
      .optional()
      .catch(undefined),
    answered_at: z.union([z.number(), z.string()]).optional().catch(undefined),
    evidence: z.record(z.unknown()).optional().catch(undefined),
  })
  .passthrough();
export type HarnessCard = z.infer<typeof harnessCardSchema>;

export const harnessEventSchema = z
  .object({
    event_id: z.string().catch(''),
    type: z.string().catch(''),
    phase: hStr,
    trigger: hStr,
    ts: hNum,
    received_at: hNum,
    session_id: hStr,
    child_agent_id: hStr,
    agent_host: hStr,
    repo: hStr,
    branch: hStr,
    pr_number: z.union([z.number(), z.string()]).optional().catch(undefined),
    /** pr_attach: `create` | `merge` */
    pr_kind: hStr,
    /** verdict events: `{source, item_id, verdict, acted_on, ...}`; its
     * free-text fields (reason, gap, context_missing) are never rendered */
    verdict: z.record(z.unknown()).optional().catch(undefined),
    card_keys: z.array(z.string()).optional().catch(undefined),
    timing: z.record(z.unknown()).optional().catch(undefined),
    detail: z.record(z.unknown()).optional().catch(undefined),
    usage_ref: hStr,
  })
  .passthrough();
export type HarnessEvent = z.infer<typeof harnessEventSchema>;

export const harnessUsageSchema = z
  .object({
    event_id: z.string().catch(''),
    event_type: hStr,
    ts: hNum,
    model: hStr,
    input_tokens: hNum,
    output_tokens: hNum,
    cache_read_tokens: hNum,
    cache_write_5m_tokens: hNum,
    cache_write_1h_tokens: hNum,
    thinking_tokens: hNum,
    llm_calls: hNum,
    turns: hNum,
    user_turns: hNum,
    tool_calls: hNum,
    hook_calls: hNum,
    wall_ms: hNum,
    agent_active_ms: hNum,
    tool_ms: hNum,
    idle_ms: hNum,
    est_cost_usd: hNum,
    priced: hBool,
    upper_bound: hBool,
  })
  .passthrough();
export type HarnessUsage = z.infer<typeof harnessUsageSchema>;

export const harnessRejectedSchema = z
  .object({
    event_id: z.string().catch(''),
    event_type: hStr,
    reason: hStr,
    received_at: hNum,
  })
  .passthrough();
export type HarnessRejected = z.infer<typeof harnessRejectedSchema>;

export const harnessSummarySchema = z
  .object({
    cards: hNum,
    events_by_type: z.record(z.unknown()).optional().catch(undefined),
    failed_events: hNum,
    rejected_events: hNum,
    rejected_by_reason: z.record(z.unknown()).optional().catch(undefined),
    harness_est_cost_usd: hNum,
    harness_active_ms: hNum,
    harness_wall_ms: hNum,
    harness_turns: hNum,
    harness_llm_calls: hNum,
    harness_input_tokens: hNum,
    harness_output_tokens: hNum,
    harness_unpriced_windows: hNum,
  })
  .passthrough();
export type HarnessSummary = z.infer<typeof harnessSummarySchema>;

export const harnessLedgerSchema = z
  .object({
    success: hBool,
    available: z.boolean().catch(false).default(false),
    captured: z.boolean().catch(false).default(false),
    session: z.record(z.unknown()).nullable().optional().catch(null),
    cards: lenientRecord(harnessCardSchema),
    events: lenientArray(harnessEventSchema),
    usage: lenientArray(harnessUsageSchema),
    rejected: lenientArray(harnessRejectedSchema),
    summary: harnessSummarySchema.optional().catch(undefined),
  })
  .passthrough();
export type HarnessLedger = z.infer<typeof harnessLedgerSchema>;

/** What an unavailable / failed harness call reads as: nothing to render. */
export const HARNESS_UNAVAILABLE: HarnessLedger = {
  available: false,
  captured: false,
  cards: {},
  events: [],
  usage: [],
  rejected: [],
};
